import React, { memo, useCallback, useMemo, useState } from "react";
import { useOrganization } from "modules/providers/organization-provider";
import { useUsers } from "modules/providers/users-provider";
import { HStack, Text } from "@chakra-ui/react";
import { useDistributionGroups } from "modules/providers/distribution-groups-provider";

import Toast from "modules/toasts/toast";
import { CopyIcon, HelpIcon } from "modules/icons";
import CircularIconButton from "./buttons/circular-icon-button";
import SyncoTooltip from "./synco-tooltip";
import colors from "app/colors";
import { useOverlay } from "modules/providers/overlay-provider";
import { MODAL_TYPE } from "modules/providers/overlay-provider/overlay-provider";
import useDisplayToast from "modules/hooks/use-display-toast";

interface IProps {
  id: string;
  color?: string;
  fontWeight?: number;
  fontSize?: string;
  displayButtons?: boolean;
  truncate?: boolean;
}

const EmailAddress = ({
  id,
  color = colors.gray50,
  fontSize = "16px",
  fontWeight = 500,
  displayButtons = true,
  truncate = false,
}: IProps) => {
  const { organization } = useOrganization();
  const { usersById } = useUsers();
  const { distributionGroupsById } = useDistributionGroups();
  const { openModal } = useOverlay();
  const { displayToast } = useDisplayToast();
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const onTooltipHover = useCallback(
    (mouseHovering: boolean) => () => {
      setIsTooltipOpen(mouseHovering);
    },
    [],
  );

  const emailAddress = useMemo(() => {
    const user = usersById[id];
    const distributionGroup = distributionGroupsById[id];
    if (organization?.emailDomain) {
      if (user) {
        return `${user.callout}@${organization.emailDomain}`;
      }
      if (distributionGroup) {
        return `${distributionGroup.name}@${organization.emailDomain}`;
      }
    }
  }, [organization?.emailDomain, id, usersById, distributionGroupsById]);

  const onHelpClicked = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();
      setIsTooltipOpen(false);
      openModal({ type: MODAL_TYPE.EMAIL_INFO });
    },
    [openModal],
  );

  const onCopyClicked = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();
      if (!emailAddress) return;
      navigator.clipboard.writeText(emailAddress);
      displayToast(() => (
        <Toast
          message="Email address copied!"
          leftIcon={<CopyIcon boxSize={5} fill={colors.grayMain} />}
        />
      ));
    },
    [emailAddress, displayToast],
  );

  if (!emailAddress) {
    return null;
  }

  return (
    <HStack spacing={0.25} w="full" overflow="hidden">
      <Text
        color={color}
        fontSize={fontSize}
        fontWeight={fontWeight}
        noOfLines={truncate ? 1 : undefined}
        flex={1}
        display={truncate ? undefined : "inline"}
        minWidth={0}
      >
        {emailAddress}
      </Text>
      <SyncoTooltip
        isOpen={isTooltipOpen}
        visibility={displayButtons ? "visible" : "hidden"}
        label={
          <React.Fragment>
            <Text>Email to Synco Address.</Text>
            <Text>Click to learn more.</Text>
          </React.Fragment>
        }
      >
        <CircularIconButton
          aria-label="email help"
          icon={<HelpIcon fill={colors.gray40} />}
          size="xs"
          onClick={onHelpClicked}
          //Need to display the tooltip in controlled mode otherwise it will persist after the modal is displayed
          onMouseOver={onTooltipHover(true)}
          onMouseOut={onTooltipHover(false)}
          visibility={displayButtons ? "visible" : "hidden"}
          flexShrink={0}
        />
      </SyncoTooltip>
      <SyncoTooltip
        label="Copy email address"
        visibility={displayButtons ? "visible" : "hidden"}
      >
        <CircularIconButton
          flexShrink={0}
          aria-label="copy email address to clipboard"
          icon={<CopyIcon fill={colors.blue} />}
          size="xs"
          visibility={displayButtons ? "visible" : "hidden"}
          onClick={onCopyClicked}
        />
      </SyncoTooltip>
    </HStack>
  );
};

export default memo(EmailAddress);
